import React from 'react';
import { Droplets, Gauge, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '../ui/utils';

interface WellStatsGridProps {
    oil: number;
    water: number;
    gas: number;
    isDarkMode: boolean;
    /** Indicates values are from tank gauge calculations */
    isCalculated?: boolean;
}

export function WellStatsGrid({ oil, water, gas, isDarkMode, isCalculated = false }: WellStatsGridProps) {
    const stats = [
        {
            label: 'Oil',
            value: oil,
            unit: 'BPD',
            icon: Droplets,
            color: 'emerald', // emerald-500
            // Light mode: bg-emerald-50 text-emerald-900 border-emerald-100
            // Dark mode: bg-emerald-950/30 text-emerald-100 border-emerald-900/50
        },
        {
            label: 'Water',
            value: water,
            unit: 'BPD',
            icon: Droplets, // Using droplets for water typically, could differentiate icon if desired
            color: 'blue',
        },
        {
            label: 'Gas',
            value: gas,
            unit: 'MCF',
            icon: Flame, // Or Gauge
            color: 'violet', // violet/purple
        },
    ];

    return (
        <div className="grid grid-cols-3 gap-3">
            {stats.map((stat, i) => (
                <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={cn(
                        "rounded-2xl p-3 border flex flex-col items-center justify-center text-center relative overflow-hidden group",
                        // Dynamic color classes based on stat.color
                        isDarkMode
                            ? `bg-${stat.color}-950/20 border-${stat.color}-900/40`
                            : `bg-${stat.color}-50 border-${stat.color}-100`
                    )}
                >
                    {/* Background decoration */}
                    <div className={cn(
                        "absolute -right-4 -top-4 w-16 h-16 rounded-full opacity-10 blur-xl transition-transform group-hover:scale-150",
                        `bg-${stat.color}-500`
                    )} />

                    <div className={cn(
                        "mb-1.5 p-1.5 rounded-full inline-flex",
                        isDarkMode
                            ? `bg-${stat.color}-900/50 text-${stat.color}-400`
                            : `bg-${stat.color}-100 text-${stat.color}-600`
                    )}>
                        <stat.icon className="w-3.5 h-3.5" />
                    </div>

                    <div className="relative z-10">
                        <span className={cn(
                            "text-lg font-bold block leading-tight tabular-nums",
                            isDarkMode ? "text-white" : `text-${stat.color}-950`
                        )}>
                            {stat.value.toFixed(1)}
                        </span>
                        <span className={cn(
                            "text-[10px] uppercase font-bold tracking-wider block mt-0.5",
                            isDarkMode ? `text-${stat.color}-400/80` : `text-${stat.color}-600/80`
                        )}>
                            {stat.unit}
                        </span>
                    </div>
                </motion.div>
            ))}
        </div>
    );
}
